// File: auth.slice.js

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { alertActions } from '../_store';
import { history, fetchWrapper } from '../_helpers';

const name = 'auth';
const initialState = createInitialState();
const reducers = createReducers();
const extraActions = createExtraActions();
const slice = createSlice({ name, initialState, reducers });

export const authActions = { ...slice.actions, ...extraActions };
export const authReducer = slice.reducer;

function createInitialState() {
    return {
        value: JSON.parse(localStorage.getItem('auth')),
        // Add new prop to hold the props passed from Login to Nav
        navProps: null,
    };
}

function createReducers() {
    return {
        setAuth,
        // Add new reducer to set the nav props
        setNavProps: (state, action) => {
            state.navProps = action.payload;
        },
        setLoginProps: (state, action) => {
            state.navProps = action.payload;
        },
    };

    function setAuth(state, action) {
        state.value = action.payload;
    }
}

function createExtraActions() {
    const baseUrl = `${process.env.REACT_APP_API_URL}`;

    return {
        otp: otp(),
        otpReg: otpReg(),
        otpMigrateValidate: otpMigrateValidate(),
        resendOtp: resendOtp(),
        login: login(),
        forgotPassword: forgotPassword(),
        passwordReset: passwordReset(),
        passwordResetMigratedUser: passwordResetMigratedUser(),
        logout: logout(),
    };

    function otp() {
        return createAsyncThunk(
            `${name}/otp`,
            async function ({ otp }, { dispatch }) {
                dispatch(alertActions.clear());
                let email = localStorage.getItem('email');
                try {
                    const session = (
                        await fetchWrapper.post(
                            `${baseUrl}/auth/verify-token`,
                            {
                                email,
                                otp,
                            }
                        )
                    )[0].session;

                    const isAdminPortal = localStorage.getItem('isAdminPortal');
                    let newAppUrl = '';

                    if (isAdminPortal) {
                        newAppUrl = `${
                            process.env.REACT_APP_ADMIN_REDIRECT_URL
                        }?session_state=${encodeURIComponent(session)}`;
                        localStorage.removeItem('isAdminPortal');
                    } else {
                        newAppUrl = `${
                            process.env.REACT_APP_REDIRECT_URL
                        }?session_state=${encodeURIComponent(
                            session
                        )}&initial=true`;
                    }

                    // Redirect to the new application URL
                    window.location.href = newAppUrl;
                    //dispatch(authActions.setAuth(user));
                    // const { from } = history.location.state || { from: { pathname: '/' } };
                    // history.navigate(from);
                } catch (error) {
                    return {
                        statusCode: 400,
                        message:
                            typeof error === 'string'
                                ? error
                                : 'Something went wrong, try again!',
                    };
                }
            }
        );
    }

    function otpReg() {
        return createAsyncThunk(
            `${name}/otp-reg`,
            async function ({ otp }, { dispatch }) {
                dispatch(alertActions.clear());
                localStorage.removeItem('registrationAuthenticated');
                let email = localStorage.getItem('email');
                try {
                    const response = await fetchWrapper.post(
                        `${baseUrl}/auth/verify-token`,
                        {
                            email,
                            otp,
                        }
                    );
                    localStorage.setItem(
                        'registrationAuthenticated',
                        JSON.stringify(response)
                    );
                    history.navigate('/kba');
                } catch (error) {
                    return {
                        statusCode: 400,
                        message:
                            typeof error === 'string'
                                ? error
                                : 'Something went wrong, try again!',
                    };
                }
            }
        );
    }

    function otpMigrateValidate() {
        return createAsyncThunk(
            `${name}/otp-migrate-validate`,
            async function ({ otp }, { dispatch }) {
                dispatch(alertActions.clear());

                let email = localStorage.getItem('email');
                try {
                    const response = await fetchWrapper.post(
                        `${baseUrl}/auth/verify-token`,
                        {
                            email,
                            otp,
                        }
                    );

                    history.navigate(
                        `/password-reset?migrated=true&id=${response[0].id}`
                    );
                } catch (error) {
                    return {
                        statusCode: 400,
                        message:
                            typeof error === 'string'
                                ? error
                                : 'Something went wrong, try again!',
                    };
                }
            }
        );
    }

    function resendOtp() {
        return createAsyncThunk(
            `${name}/resend-otp`,
            async function ({ email }, { dispatch }) {
                dispatch(alertActions.clear());

                try {
                    await fetchWrapper.post(`${baseUrl}/auth/resend-otp`, {
                        email: email,
                    });
                } catch (error) {
                    return {
                        statusCode: 400,
                        message:
                            typeof error === 'string'
                                ? error
                                : 'Something went wrong, try again!',
                    };
                }
            }
        );
    }

    function login() {
        return createAsyncThunk(
            `${name}/login`,
            async function ({ username, password }, { dispatch }) {
                dispatch(alertActions.clear());
                // localStorage.removeItem('email');

                try {
                    const userAuth = await fetchWrapper.post(
                        `${baseUrl}/auth/token`,
                        {
                            username,
                            password,
                        }
                    );

                    localStorage.setItem('email', username);

                    if (userAuth.hasToResetPassword) {
                        history.navigate(
                            `/otp-verify?migrated=true&id=${userAuth.userId}`
                        );
                    } else {
                        history.navigate('/otp-verify');
                    }
                } catch (error) {
                    return {
                        statusCode: 400,
                        message:
                            typeof error === 'string'
                                ? error
                                : 'Something went wrong, try again!',
                    };
                }
            }
        );
    }

    function forgotPassword() {
        return createAsyncThunk(
            `${name}/forgot-password`,
            async function ({ username }, { getState, dispatch }) {
                dispatch(alertActions.clear());

                const reqBody = {
                    email: username,
                };
                try {
                    await fetchWrapper.post(
                        `${baseUrl}/password-reset/get/token`,
                        reqBody
                    );

                    return {
                        statusCode: 200,
                        message: 'Successful',
                    };
                } catch (error) {
                    return {
                        statusCode: 400,
                        message:
                            typeof error === 'string'
                                ? error
                                : 'Something went wrong, try again!',
                    };
                }
            }
        );
    }

    function passwordReset() {
        return createAsyncThunk(
            `${name}/password-reset`,
            async function ({ password, token, id }, { dispatch }) {
                dispatch(alertActions.clear());

                const reqBody = {
                    id: id,
                    password: password,
                    passwordResetToken: token,
                };

                try {
                    await fetchWrapper.post(
                        `${baseUrl}/password-reset`,
                        reqBody
                    );
                } catch (error) {
                    return {
                        statusCode: 400,
                        message:
                            typeof error === 'string'
                                ? error
                                : 'Something went wrong, try again!',
                    };
                }
            }
        );
    }

    function passwordResetMigratedUser() {
        return createAsyncThunk(
            `${name}/password-reset-migrated-user`,
            async function ({ password, id }, { dispatch }) {
                dispatch(alertActions.clear());

                const reqBody = {
                    id: id,
                    password: password,
                };

                try {
                    await fetchWrapper.post(
                        `${baseUrl}/password-reset/migrated-user/password-reset`,
                        reqBody
                    );
                } catch (error) {
                    return {
                        statusCode: 400,
                        message:
                            typeof error === 'string'
                                ? error
                                : 'Something went wrong, try again!',
                    };
                }
            }
        );
    }

    function logout() {
        return createAsyncThunk(`${name}/logout`, function (arg, { dispatch }) {
            dispatch(authActions.setAuth(null));
            localStorage.removeItem('auth');
            history.navigate('/login');
        });
    }
}
